<template>
  <div class="address-search" :class="{ focused: open, invalid: invalid }" ref="addressSearch" tabindex="-1" @focusout="checkFocusOut($event)">
    <div class="selected" :class="{ open: open && options.length }">
      <input
        type="text"
        ref="addressSearchInput"
        v-model="addressText"
        :tabindex="tabindex"
        @keydown="moveOption($event)"
        @focus="
          open = true;
          focusedIndex = null;
          oldText = addressText;
        " />
      <label>{{ label }}</label>
      <img v-if="showExtLink" src="../../assets/external-link.svg" alt="" class="external-link" @click="$emit('navigateToLink')" />
    </div>
    <div class="items" ref="addressSearchItems" :class="{ selectHide: !open }">
      <div v-for="(option, i) of options" :key="i" @click="selectOption(option)" :class="{ hovering: focusedIndex == i }" @mouseover="focusedIndex = i" @mouseleave="focusedIndex = null">
        {{ option.formattedAddress }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddressSearch',
  emits: ['update:modelValue', 'autofillAddress', 'clearLookup', 'navigateToLink'],
  props: {
    options: {
      type: Array,
      required: true,
    },
    modelValue: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    tabindex: {
      type: String,
      required: false,
      default: '0',
    },
    showExtLink: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      focusedIndex: null,
      oldText: '',
    };
  },
  computed: {
    addressText: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },
  methods: {
    selectOption(option, dontClose) {
      this.$emit('autofillAddress', option);
      if (!dontClose) {
        this.open = false;
        this.$emit('clearLookup');
      }
    },
    checkFocusOut(event) {
      if (!this.$refs.addressSearchItems.contains(event.relatedTarget) && !this.$refs.addressSearch.contains(event.relatedTarget)) {
        this.open = false;
        this.$emit('clearLookup');
      }
    },
    moveOption(event) {
      let direction = '';
      if (event.key === 'ArrowDown') direction = 'down';
      else if (event.key === 'ArrowUp') {
        direction = 'up';
        event.preventDefault();
      } else if (event.key === 'Enter' && this.focusedIndex !== null) {
        this.selectOption(this.options[this.focusedIndex]);
        return;
      } else {
        this.focusedIndex = null;
        this.updateOldText();
        return;
      }

      if (this.focusedIndex === null && direction == 'down') this.focusedIndex = 0;
      else {
        this.focusedIndex = direction === 'down' ? this.focusedIndex + 1 : this.focusedIndex - 1;
      }

      if (this.focusedIndex < 0) this.focusedIndex = null;
      if (this.focusedIndex >= this.options.length) this.focusedIndex = this.options.length - 1;

      if (this.focusedIndex >= 0 && this.focusedIndex < this.options.length && this.focusedIndex !== null) {
        const newSelectedOption = this.options[this.focusedIndex];
        this.selectOption(newSelectedOption, true);
      } else if (this.focusedIndex === null) {
        this.addressText = this.oldText;
      }
    },
    updateOldText() {
      setTimeout(() => {
        this.oldText = this.addressText;
      }, 10);
    },
  },
};
</script>

<style lang="scss" scoped>
.address-search {
  position: relative;
  text-align: left;
  outline: none;
  position: relative;

  &.invalid .selected {
    border: $alertColor 2px solid;

    input {
      padding: calc(0.85rem - 1px) calc(0.7rem - 1px) calc(0.7rem - 1px) calc(0.7rem - 1px);
    }

    label {
      color: $alertColor;
    }
  }

  &.focused .selected {
    border: solid 2px $primaryActionColor;

    input {
      padding: calc(0.85rem - 1px) calc(0.7rem - 1px) calc(0.7rem - 1px) calc(0.7rem - 1px);
    }

    label {
      color: $primaryActionColor;
    }
  }

  .selected {
    background-color: white;
    border-radius: $borderRadius4;
    border: 1px solid $inputBorderColor;
    cursor: pointer;
    user-select: none;
    transition: border-color 0.15s ease-in-out;

    input {
      border-radius: $borderRadius4;
      padding: 0.85rem 0.7rem 0.7rem 0.7rem;
      user-select: none;
      color: $textColor87;
      line-height: 1.5;
      outline: none;
      border: none;
      font-size: $fontSize14;
      width: 100%;
      box-sizing: border-box;
    }

    &:disabled {
      color: $textColor12;
    }

    &.open {
      border-radius: $borderRadius4 $borderRadius4 0px 0px;
    }

    label {
      position: absolute;
      top: -0.32rem;
      left: 0.55rem;
      font-size: $fontSize10;
      color: $textColor60;
      background: white;
      padding: 0 0.3rem;
      border-radius: $borderRadius10;
      line-height: 1;
    }
  }

  .items {
    color: $textColor87;
    border-radius: 0px 0px $borderRadius4 $borderRadius4;
    overflow: hidden;
    position: absolute;
    background-color: white;
    left: 0;
    right: 0;
    z-index: 1;
    border: 1px solid $inputBorderColor;
    border-top: none;
    max-height: 25rem;
    overflow-y: auto;

    div {
      color: $textColor87;
      font-size: $fontSize11;
      padding: 0.6rem 0.7rem 0.6rem 0.7rem;
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;

      img {
        margin-right: 0.5rem;
      }

      &.hovering {
        background-color: $arrowGrayColor;
      }
    }
  }
}

.external-link {
  position: absolute;
  top: 0.75rem;
  right: 0.8rem;
  height: 1.5rem;
  cursor: pointer;
}

.selectHide {
  display: none;
}
</style>
